/**
 * /time — time entry surface with TWO views:
 *
 *   List   — flat grid of all entries with filters; good for approval +
 *            audit ("show me all submitted entries by Alice in March")
 *   Week   — spreadsheet-style grid for ONE employee × ONE week; good
 *            for daily entry by the worker themselves
 *
 * The view toggle is a tab at the top. Both views share the same data
 * (the weekly grid scopes its query to one employee + week).
 *
 * Three operator workflows this page covers:
 *   1. Daily entry: switch to Week view → pick employee → fill cells
 *   2. Approver: List view, filter status=submitted → click ✓ on each
 *   3. End-of-period: click "Run accrual" → all approved entries → JE
 */
import { createFileRoute } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  DataGridPro,
  type GridColDef,
  type GridRenderCellParams,
} from "@mui/x-data-grid-pro";
import { Chip, Tab, Tabs } from "@mui/material";

import { api } from "@/lib/api";
import { FlexGrid, gridDefaults } from "@/grids/gridDefaults";
import {
  employeesListQueryOptions,
  personnelKeys,
  timeEntriesListQueryOptions,
} from "@/lib/queries";
import { useToast } from "@/components/Toaster";
import { useConfirmAction } from "@/components/ConfirmDialog";
import { TimeEntryFormDialog } from "@/components/TimeEntryFormDialog";
import { WeeklyTimeGrid } from "@/components/WeeklyTimeGrid";
import type {
  AccrueResponse,
  TimeEntry,
  TimeEntryStatus,
} from "@/lib/types";

export const Route = createFileRoute("/_auth/time")({
  component: TimePage,
});

const STATUS_COLOR: Record<TimeEntryStatus, "default" | "info" | "warning" | "success" | "error"> = {
  draft: "default",
  submitted: "info",
  approved: "warning",   // amber — "ready to accrue"
  posted: "success",
  rejected: "error",
};

function TimePage() {
  const qc = useQueryClient();
  const toast = useToast();
  const confirmAction = useConfirmAction();

  const [view, setView] = useState<"list" | "week">("list");
  const [employeeFilter, setEmployeeFilter] = useState<string>("");
  const [statusFilter, setStatusFilter] = useState<TimeEntryStatus | "">("");
  const [formOpen, setFormOpen] = useState(false);
  // Weekly grid state — both default to today's Monday + the first
  // active employee. They live here (not in WeeklyTimeGrid) so the
  // top-of-page filter row can drive them.
  const [weekStart, setWeekStart] = useState<string>(() => {
    const d = new Date();
    const dow = d.getDay(); // 0 = Sun
    const offset = dow === 0 ? -6 : 1 - dow; // back to Monday
    d.setDate(d.getDate() + offset);
    return d.toISOString().slice(0, 10);
  });

  const employeesQuery = useQuery(employeesListQueryOptions());
  const entriesQuery = useQuery(
    timeEntriesListQueryOptions({
      employee_id: employeeFilter || undefined,
      status: statusFilter || undefined,
    }),
  );

  const submit = useMutation({
    mutationFn: async (id: string) =>
      api(`/api/personnel/time-entries/${id}/submit`, { method: "POST" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: personnelKeys.all }),
    onError: (err: Error) => toast.error(`Submit failed: ${err.message}`),
  });

  const approve = useMutation({
    mutationFn: async (id: string) =>
      api<TimeEntry>(`/api/personnel/time-entries/${id}/approve`, {
        method: "POST",
      }),
    onSuccess: (entry) => {
      qc.invalidateQueries({ queryKey: personnelKeys.all });
      // Layer 1.6: differentiate partial vs final approval. If the entry
      // is still SUBMITTED after the call, only one of two signatures
      // has been collected; tell the operator another signature is owed.
      if (entry.status === "submitted") {
        toast.success(
          `Signature recorded (${entry.approvals_log.length}/2). ` +
          `Needs one more approver before it flips to APPROVED.`,
        );
      } else {
        toast.success("Approved");
      }
    },
    onError: (err: Error) => toast.error(`Approve failed: ${err.message}`),
  });

  const reject = useMutation({
    mutationFn: async (vars: { id: string; reason: string }) =>
      api(`/api/personnel/time-entries/${vars.id}/reject`, {
        method: "POST",
        body: { reason: vars.reason },
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: personnelKeys.all }),
    onError: (err: Error) => toast.error(`Reject failed: ${err.message}`),
  });

  const accrue = useMutation({
    mutationFn: async (through: string) =>
      api<AccrueResponse>("/api/personnel/time-entries/accrue", {
        method: "POST",
        body: { through_date: through },
      }),
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: personnelKeys.all });
      if (r.journal_id) {
        toast.success(
          `Accrued ${r.entry_count} entries → journal #${r.journal_no} ($${r.total_labor_cost} labor + $${r.total_burden_cost} burden)`,
        );
      } else {
        toast.success(`No new approved entries to accrue${r.skipped_reason ? ` (${r.skipped_reason})` : ""}`);
      }
    },
    onError: (err: Error) => toast.error(`Accrual failed: ${err.message}`),
  });

  const requestReject = (e: TimeEntry) => {
    const reason = window.prompt(
      `Reject ${e.employee_name}'s ${e.hours}h entry on ${e.work_date}?\nReason:`,
      "",
    );
    if (reason && reason.trim()) reject.mutate({ id: e.id, reason: reason.trim() });
  };

  const requestAccrue = () => {
    const today = new Date().toISOString().slice(0, 10);
    confirmAction({
      title: "Run labor accrual?",
      message:
        `This will post a journal that:\n` +
        `  • Dr Job Cost - Labor (5200) per project/phase/cost_type\n` +
        `  • Dr Job Cost - Burden (5210) for any employee with burden_pct > 0\n` +
        `  • Cr Payroll Liabilities (2140)\n\n` +
        `Through date: ${today}\n\n` +
        `Every APPROVED entry through that date will flip to POSTED.`,
      confirmLabel: "Run accrual",
      kind: "primary",
      onConfirm: async () => { await accrue.mutateAsync(today); },
    });
  };

  const columns = useMemo<GridColDef<TimeEntry>[]>(() => [
    { field: "work_date", headerName: "Date", width: 110 },
    { field: "employee_name", headerName: "Employee", width: 180 },
    {
      field: "hours",
      headerName: "Hours",
      width: 80,
      renderCell: (p) => (
        <span className="font-mono">{Number(p.row.hours).toFixed(2)}</span>
      ),
    },
    {
      field: "rate_at_entry",
      headerName: "Rate",
      width: 90,
      renderCell: (p) => (
        <span className="font-mono text-xs text-slate-600 dark:text-slate-300">
          ${Number(p.row.rate_at_entry).toFixed(2)}
        </span>
      ),
    },
    {
      field: "labor_cost",
      headerName: "Labor cost",
      width: 110,
      renderCell: (p) => (
        <span className="font-mono">
          ${Number(p.row.labor_cost).toLocaleString(undefined, {
            minimumFractionDigits: 2, maximumFractionDigits: 2,
          })}
        </span>
      ),
    },
    {
      field: "cost_type",
      headerName: "Cost type",
      width: 110,
      renderCell: (p) => (
        <span className="text-xs text-slate-600 dark:text-slate-300">
          {p.row.cost_type ?? "—"}
        </span>
      ),
    },
    {
      field: "status",
      headerName: "Status",
      width: 160,
      renderCell: (p: GridRenderCellParams<TimeEntry>) => {
        const e = p.row;
        // Layer 1.6 — show partial-approval signature count when an entry
        // has begun a two-step approval but isn't fully signed off yet.
        const partialSigs =
          e.status === "submitted" && (e.approvals_log?.length ?? 0) > 0;
        return (
          <div className="flex items-center gap-1">
            <Chip
              label={e.status}
              size="small"
              color={STATUS_COLOR[e.status]}
              sx={{ fontSize: 10, height: 20 }}
            />
            {partialSigs && (
              <Chip
                label={`${e.approvals_log.length}/2`}
                size="small"
                title={
                  `Two-step approval. Signed by ` +
                  e.approvals_log.map((a) => a.user_id).join(", ") +
                  `. Needs one more signature.`
                }
                sx={{
                  fontSize: 10, height: 20,
                  bgcolor: "#fef3c7", color: "#854d0e",
                }}
              />
            )}
            {e.is_overtime && (
              <span title="Overtime" className="text-amber-600">⚡</span>
            )}
            {e.pto_type && (
              <span title={`PTO: ${e.pto_type}`} className="text-emerald-600">🌴</span>
            )}
          </div>
        );
      },
    },
    { field: "notes", headerName: "Notes", width: 200 },
    {
      field: "_actions",
      headerName: "",
      width: 200,
      sortable: false,
      filterable: false,
      renderCell: (p: GridRenderCellParams<TimeEntry>) => {
        const e = p.row;
        return (
          <div className="flex items-center gap-1">
            {e.status === "draft" && (
              <button
                onClick={() => submit.mutate(e.id)}
                className="text-xs text-blue-600 hover:text-blue-800"
              >
                Submit
              </button>
            )}
            {e.status === "submitted" && (
              <>
                <button
                  onClick={() => approve.mutate(e.id)}
                  className="text-xs text-emerald-600 hover:text-emerald-800"
                >
                  ✓ Approve
                </button>
                <button
                  onClick={() => requestReject(e)}
                  className="text-xs text-red-600 hover:text-red-800"
                >
                  ✗ Reject
                </button>
              </>
            )}
            {e.status === "rejected" && e.rejection_reason && (
              <span
                className="text-xs text-red-600"
                title={e.rejection_reason}
              >
                {e.rejection_reason.slice(0, 30)}…
              </span>
            )}
            {e.status === "posted" && (
              <span
                className="text-xs text-emerald-700"
                title={`Journal: ${e.posted_journal_id}`}
              >
                ✓ Posted
              </span>
            )}
          </div>
        );
      },
    },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [approve, submit]);

  const approvedCount = (entriesQuery.data ?? []).filter((e) => e.status === "approved").length;
  const partialApprovalCount = (entriesQuery.data ?? []).filter(
    (e) => e.status === "submitted" && (e.approvals_log?.length ?? 0) > 0,
  ).length;

  // For the weekly view we need the chosen employee Object to pass into
  // <WeeklyTimeGrid>. employeeFilter is the selected employee_id; if it's
  // empty we default to the first active employee so the grid renders
  // immediately rather than showing an empty state.
  const weekEmployee = useMemo(() => {
    const list = employeesQuery.data ?? [];
    if (employeeFilter) return list.find((e) => e.id === employeeFilter) ?? null;
    return list.find((e) => e.employment_status === "active") ?? null;
  }, [employeeFilter, employeesQuery.data]);

  const shiftWeek = (deltaDays: number) => {
    const d = new Date(weekStart + "T00:00:00");
    d.setDate(d.getDate() + deltaDays);
    setWeekStart(d.toISOString().slice(0, 10));
  };

  return (
    <div className="flex flex-col h-[calc(100vh-56px)] overflow-hidden">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 px-6 py-3 dark:bg-gray-900 dark:border-gray-800">
        <div className="flex items-center gap-3">
          <h1 className="text-xl font-semibold text-slate-900 dark:text-slate-100">Time</h1>
          <span className="text-xs text-slate-500 dark:text-slate-400">
            {view === "list"
              ? `${entriesQuery.data?.length ?? 0} entries`
              : weekEmployee
                ? `Week of ${weekStart}`
                : "Pick an employee"}
            {view === "list" && approvedCount > 0 && (
              <span className="text-amber-700 ml-2">
                · {approvedCount} approved + ready to accrue
              </span>
            )}
            {view === "list" && partialApprovalCount > 0 && (
              <span className="text-amber-800 ml-2" title="Two-step approval started but not yet complete">
                · {partialApprovalCount} awaiting 2nd signature
              </span>
            )}
          </span>
          <div className="flex-1" />

          {/* Employee picker — both views share */}
          <select
            value={employeeFilter}
            onChange={(e) => setEmployeeFilter(e.target.value)}
            className="text-sm border border-slate-300 rounded-md px-2 py-1.5 dark:border-gray-700"
          >
            <option value="">
              {view === "week" ? "(first active employee)" : "All employees"}
            </option>
            {(employeesQuery.data ?? []).map((e) => (
              <option key={e.id} value={e.id}>{e.name}</option>
            ))}
          </select>

          {view === "list" && (
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as TimeEntryStatus | "")}
              className="text-sm border border-slate-300 rounded-md px-2 py-1.5 dark:border-gray-700"
            >
              <option value="">All statuses</option>
              <option value="draft">Draft</option>
              <option value="submitted">Submitted</option>
              <option value="approved">Approved</option>
              <option value="posted">Posted</option>
              <option value="rejected">Rejected</option>
            </select>
          )}

          {view === "week" && (
            <div className="flex items-center gap-1">
              <button
                onClick={() => shiftWeek(-7)}
                className="text-sm border border-slate-300 rounded-md px-2 py-1.5 hover:bg-slate-50 dark:border-gray-700 dark:hover:bg-gray-800"
                title="Previous week"
              >
                ◀
              </button>
              <input
                type="date"
                value={weekStart}
                onChange={(e) => setWeekStart(e.target.value)}
                className="text-sm border border-slate-300 rounded-md px-2 py-1.5 dark:border-gray-700"
              />
              <button
                onClick={() => shiftWeek(7)}
                className="text-sm border border-slate-300 rounded-md px-2 py-1.5 hover:bg-slate-50 dark:border-gray-700 dark:hover:bg-gray-800"
                title="Next week"
              >
                ▶
              </button>
            </div>
          )}

          <button
            onClick={requestAccrue}
            disabled={accrue.isPending}
            className="text-sm bg-amber-600 hover:bg-amber-700 text-white rounded-md px-3 py-1.5 font-medium disabled:opacity-50"
            title="Accrue all approved-but-unposted entries through today"
          >
            {accrue.isPending ? "Accruing…" : "🧾 Run accrual"}
          </button>
          <button
            onClick={() => setFormOpen(true)}
            className="text-sm bg-blue-600 hover:bg-blue-700 text-white rounded-md px-3 py-1.5 font-medium"
          >
            + New entry
          </button>
        </div>
      </div>

      {/* View toggle */}
      <div className="bg-white border-b border-slate-200 px-6 dark:bg-gray-900 dark:border-gray-800">
        <Tabs
          value={view}
          onChange={(_e, v) => setView(v as "list" | "week")}
          sx={{ minHeight: 40 }}
        >
          <Tab value="list" label="All entries" sx={{ minHeight: 40 }} />
          <Tab value="week" label="Weekly grid" sx={{ minHeight: 40 }} />
        </Tabs>
      </div>

      <TimeEntryFormDialog
        open={formOpen}
        onClose={() => setFormOpen(false)}
        defaultEmployeeId={employeeFilter || weekEmployee?.id}
      />

      <div className="flex-1 min-h-0 px-6 py-4">
        {view === "list" && (
          <FlexGrid>
            <DataGridPro
              {...gridDefaults}
              rows={entriesQuery.data ?? []}
              columns={columns}
              getRowId={(r) => r.id}
              loading={entriesQuery.isLoading}
              density="compact"
              disableRowSelectionOnClick
            />
          </FlexGrid>
        )}
        {view === "week" && weekEmployee && (
          <div className="bg-white border border-slate-200 rounded-lg h-full overflow-hidden dark:bg-gray-900 dark:border-gray-800">
            <WeeklyTimeGrid
              employee={weekEmployee}
              weekStart={weekStart}
            />
          </div>
        )}
        {view === "week" && !weekEmployee && (
          <div className="text-center text-sm text-slate-500 py-12 dark:text-slate-400">
            No active employees. Add one in /employees first.
          </div>
        )}
      </div>
    </div>
  );
}
